/**
 * Finger Performance Block - Widget 4
 * 
 * Shows performance analysis for each finger with visual hand layout
 * Includes accuracy, speed, strength scores, and improvement rates
 */

import React, { useState, useMemo } from 'react';
import { FingerPerformanceData } from '@/types/statistics';

interface FingerPerformanceBlockProps {
  data: FingerPerformanceData[];
  loading: boolean;
}

type ViewMode = 'visual' | 'table';

const FingerPerformanceBlock: React.FC<FingerPerformanceBlockProps> = ({ 
  data, 
  loading 
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('visual');

  // Separate data by hand
  const handData = useMemo(() => {
    const leftFingers = data.filter(finger => finger.hand === 'left');
    const rightFingers = data.filter(finger => finger.hand === 'right');
    
    // Order fingers from pinky to thumb
    const fingerOrder = ['Pinky', 'Ring', 'Middle', 'Index', 'Thumb'];
    
    const orderFingers = (fingers: FingerPerformanceData[]) => {
      return fingerOrder.map(fingerName => 
        fingers.find(f => f.finger === fingerName)
      ).filter(Boolean) as FingerPerformanceData[];
    };

    return {
      left: orderFingers(leftFingers),
      right: orderFingers(rightFingers).reverse() // Reverse for visual layout
    };
  }, [data]);

  // Calculate overall hand performance
  const handStats = useMemo(() => {
    const calculateHandStats = (fingers: FingerPerformanceData[]) => {
      if (fingers.length === 0) return { accuracy: 0, avgSpeed: 0, totalPractice: 0 };
      
      const totalPractice = fingers.reduce((sum, f) => sum + f.practiceCount, 0);
      const weightedAccuracy = fingers.reduce((sum, f) => sum + (f.accuracy * f.practiceCount), 0) / totalPractice;
      const weightedSpeed = fingers.reduce((sum, f) => sum + (f.avgSpeed * f.practiceCount), 0) / totalPractice;
      
      return {
        accuracy: weightedAccuracy,
        avgSpeed: weightedSpeed,
        totalPractice
      };
    };

    return {
      left: calculateHandStats(handData.left),
      right: calculateHandStats(handData.right)
    };
  }, [handData]);

  if (loading) {
    return <FingerPerformanceSkeletonBlock />;
  }

  if (data.length === 0) {
    return <EmptyFingerPerformanceBlock />;
  }

  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            🖐️ Finger Performance
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            Analysis by finger and hand • {data.length} fingers tracked
          </p>
        </div>

        {/* View Mode Toggle */}
        <div style={{ display: 'flex', background: '#334155', borderRadius: '6px', padding: '2px' }}>
          <button
            onClick={() => setViewMode('visual')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              background: viewMode === 'visual' ? '#475569' : 'transparent',
              color: '#e2e8f0'
            }}
          >
            👁️ Visual
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              background: viewMode === 'table' ? '#475569' : 'transparent',
              color: '#e2e8f0'
            }}
          >
            📋 Table
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'visual' ? (
        <VisualHandLayout handData={handData} handStats={handStats} />
      ) : (
        <TableView data={data} />
      )}

      {/* Summary Stats */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #475569' }}>
        <SummaryStats data={data} handStats={handStats} />
      </div>
    </div>
  );
};

// Visual Hand Layout Component
const VisualHandLayout: React.FC<{
  handData: { left: FingerPerformanceData[]; right: FingerPerformanceData[] };
  handStats: any;
}> = ({ handData, handStats }) => {

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Hand Performance Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
        <HandSummaryCard 
          hand="left" 
          emoji="👈" 
          stats={handStats.left}
          fingers={handData.left}
        />
        <HandSummaryCard 
          hand="right" 
          emoji="👉" 
          stats={handStats.right}
          fingers={handData.right}
        />
      </div>

      {/* Visual Hand Layout */}
      <div style={{ background: '#334155', borderRadius: '8px', padding: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '16px', textAlign: 'center' }}>
          🖐️ Hand Performance Visualization
        </h3>
        
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'end', gap: '32px', flexWrap: 'wrap' }}>
          {/* Left Hand */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '12px' }}>Left Hand</h4>
            <div style={{ display: 'flex', gap: '6px' }}>
              {handData.left.map((finger, index) => (
                <FingerBar 
                  key={finger.finger} 
                  finger={finger} 
                  position={index}
                  hand="left"
                />
              ))}
            </div>
          </div>

          {/* Right Hand */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h4 style={{ fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '12px' }}>Right Hand</h4>
            <div style={{ display: 'flex', gap: '6px' }}>
              {handData.right.map((finger, index) => (
                <FingerBar 
                  key={finger.finger} 
                  finger={finger} 
                  position={index}
                  hand="right"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '10px', color: '#94a3b8', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '2px' }}></div>
              <span>95%+ Accuracy</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '2px' }}></div>
              <span>90-95% Accuracy</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '12px', height: '12px', background: '#f59e0b', borderRadius: '2px' }}></div>
              <span>85-90% Accuracy</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' }}></div>
              <span>&lt;85% Accuracy</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual Finger Bar Component
const FingerBar: React.FC<{
  finger: FingerPerformanceData;
  position: number;
  hand: 'left' | 'right';
}> = ({ finger }) => {

  const getFingerHeight = (fingerName: string): number => {
    const heights = {
      'Pinky': 60,
      'Ring': 80,
      'Middle': 90,
      'Index': 85,
      'Thumb': 65
    };
    return heights[fingerName as keyof typeof heights] || 70;
  };

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 95) return 'bg-green-500';
    if (accuracy >= 90) return 'bg-blue-500';
    if (accuracy >= 85) return 'bg-yellow-500';
    return 'bg-red-500';
  };



  const fingerHeight = getFingerHeight(finger.finger);
  const accuracyColor = getAccuracyColor(finger.accuracy);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      {/* Finger Visualization */}
      <div 
        style={{
          width: '24px',
          height: `${fingerHeight}px`,
          background: accuracyColor === 'bg-green-500' ? '#10b981' :
                     accuracyColor === 'bg-blue-500' ? '#3b82f6' :
                     accuracyColor === 'bg-yellow-500' ? '#f59e0b' : '#ef4444',
          borderRadius: '8px 8px 0 0',
          opacity: Math.max(0.3, Math.min(1, finger.strengthScore / 10)),
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'end',
          paddingBottom: '4px',
          gap: '2px'
        }}
      >
        {/* Strength indicator dots */}
        {Array.from({ length: Math.round(finger.strengthScore / 2) }).map((_, i) => (
          <div key={i} style={{ width: '2px', height: '2px', background: '#ffffff', borderRadius: '50%', opacity: '0.8' }}></div>
        ))}
      </div>

      {/* Finger Label */}
      <div style={{ marginTop: '6px', fontSize: '9px', fontWeight: '500', color: '#e2e8f0', textAlign: 'center' }}>
        {finger.finger}
      </div>

      {/* Accuracy Label */}
      <div style={{ fontSize: '8px', color: '#94a3b8', textAlign: 'center' }}>
        {finger.accuracy.toFixed(1)}%
      </div>
    </div>
  );
};

// Hand Summary Card Component
const HandSummaryCard: React.FC<{
  hand: 'left' | 'right';
  emoji: string;
  stats: any;
  fingers: FingerPerformanceData[];
}> = ({ hand, emoji, stats, fingers }) => {



  const getPerformanceColorStyle = (accuracy: number) => {
    if (accuracy >= 95) return { color: '#166534', background: '#dcfce7' };
    if (accuracy >= 90) return { color: '#1e40af', background: '#dbeafe' };
    if (accuracy >= 85) return { color: '#92400e', background: '#fef3c7' };
    return { color: '#dc2626', background: '#fee2e2' };
  };

  const strongestFinger = fingers.reduce((strongest, current) => 
    current.strengthScore > strongest.strengthScore ? current : strongest,
    fingers[0]
  );

  const weakestFinger = fingers.reduce((weakest, current) => 
    current.strengthScore < weakest.strengthScore ? current : weakest,
    fingers[0]
  );

  return (
    <div style={{ background: '#334155', borderRadius: '6px', padding: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '24px' }}>{emoji}</span>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0', textTransform: 'capitalize' }}>{hand} Hand</h3>
          <p style={{ fontSize: '10px', color: '#94a3b8' }}>{fingers.length} fingers active</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Overall Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              padding: '6px 8px',
              borderRadius: '6px',
              ...getPerformanceColorStyle(stats.accuracy)
            }}>
              {stats.accuracy.toFixed(1)}%
            </div>
            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>Accuracy</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>
              {stats.avgSpeed.toFixed(0)}ms
            </div>
            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>Avg Speed</div>
          </div>
        </div>

        {/* Finger Breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h4 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0' }}>Performance by Finger</h4>
          {fingers.map(finger => (
            <div key={finger.finger} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
              <span style={{ color: '#94a3b8' }}>{finger.finger}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: '500',
                  ...getPerformanceColorStyle(finger.accuracy)
                }}>
                  {finger.accuracy.toFixed(1)}%
                </span>
                <span style={{ color: '#64748b', width: '32px', textAlign: 'right' }}>
                  {finger.avgSpeed.toFixed(0)}ms
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Strongest/Weakest */}
        {strongestFinger && weakestFinger && (
          <div style={{ borderTop: '1px solid #475569', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
              <span style={{ color: '#16a34a', fontWeight: '500' }}>💪 Strongest:</span>
              <span style={{ color: '#e2e8f0' }}>{strongestFinger.finger}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
              <span style={{ color: '#ca8a04', fontWeight: '500' }}>🎯 Focus on:</span>
              <span style={{ color: '#e2e8f0' }}>{weakestFinger.finger}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Table View Component
const TableView: React.FC<{ data: FingerPerformanceData[] }> = ({ data }) => {

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 95) return 'text-green-600 bg-green-50';
    if (accuracy >= 90) return 'text-blue-600 bg-blue-50';
    if (accuracy >= 85) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getImprovementIcon = (rate: number): string => {
    if (rate > 5) return '📈';
    if (rate < -5) return '📉';
    return '📊';
  };

  const getImprovementColor = (rate: number): string => {
    if (rate > 5) return 'text-green-600';
    if (rate < -5) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-2 font-medium text-gray-700">Finger</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Hand</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Practice</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Accuracy</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Speed</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Strength</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Trend</th>
            <th className="text-left py-3 px-2 font-medium text-gray-700">Letters</th>
          </tr>
        </thead>
        <tbody>
          {data.map((finger) => (
            <tr key={`${finger.hand}-${finger.finger}`} className="border-b border-gray-100 hover:bg-gray-50">
              {/* Finger */}
              <td className="py-3 px-2">
                <span className="font-medium text-gray-900">{finger.finger}</span>
              </td>

              {/* Hand */}
              <td className="py-3 px-2">
                <span className={`
                  px-2 py-1 rounded-md text-xs font-medium
                  ${finger.hand === 'left' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}
                `}>
                  {finger.hand === 'left' ? '👈 Left' : '👉 Right'}
                </span>
              </td>

              {/* Practice Count */}
              <td className="py-3 px-2 text-gray-700">
                {finger.practiceCount.toLocaleString()}
              </td>

              {/* Accuracy */}
              <td className="py-3 px-2">
                <span className={`
                  font-medium px-2 py-1 rounded-md text-sm
                  ${getAccuracyColor(finger.accuracy)}
                `}>
                  {finger.accuracy.toFixed(1)}%
                </span>
              </td>

              {/* Speed */}
              <td className="py-3 px-2">
                <span className="font-medium text-gray-900">
                  {finger.avgSpeed.toFixed(0)}ms
                </span>
              </td>

              {/* Strength Score */}
              <td className="py-3 px-2">
                <div className="flex items-center gap-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${(finger.strengthScore / 10) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {finger.strengthScore.toFixed(1)}
                  </span>
                </div>
              </td>

              {/* Improvement Rate */}
              <td className="py-3 px-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getImprovementIcon(finger.improvementRate)}</span>
                  <span className={`text-sm font-medium ${getImprovementColor(finger.improvementRate)}`}>
                    {finger.improvementRate > 0 ? '+' : ''}{finger.improvementRate.toFixed(1)}%
                  </span>
                </div>
              </td>

              {/* Assigned Letters */}
              <td className="py-3 px-2">
                <div className="text-sm text-gray-600 font-mono max-w-32">
                  {finger.assignedLetters.slice(0, 6).join(', ')}
                  {finger.assignedLetters.length > 6 && '...'}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Summary Stats Component
const SummaryStats: React.FC<{
  data: FingerPerformanceData[];
  handStats: any;
}> = ({ data }) => {

  const overallAccuracy = data.length > 0 
    ? data.reduce((sum, finger) => sum + finger.accuracy, 0) / data.length 
    : 0;

  const overallSpeed = data.length > 0
    ? data.reduce((sum, finger) => sum + finger.avgSpeed, 0) / data.length
    : 0;

  const totalPractice = data.reduce((sum, finger) => sum + finger.practiceCount, 0);

  const improvingFingers = data.filter(finger => finger.improvementRate > 2).length;


  return (
    <div>
      <h3 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>Overall Summary</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px' }}>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>{data.length}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Active Fingers</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>{overallAccuracy.toFixed(1)}%</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Avg Accuracy</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#8b5cf6' }}>{overallSpeed.toFixed(0)}ms</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Avg Speed</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#16a34a' }}>{improvingFingers}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Improving</div>
        </div>
        <div style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ca8a04' }}>{totalPractice.toLocaleString()}</div>
          <div style={{ fontSize: '9px', color: '#94a3b8' }}>Total Practice</div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const FingerPerformanceSkeletonBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <div>
        <div style={{ height: '16px', background: '#475569', borderRadius: '4px', width: '180px', marginBottom: '8px' }}></div>
        <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '200px' }}></div>
      </div>
      <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '80px' }}></div>
    </div>
    
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', marginBottom: '16px' }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} style={{ background: '#334155', borderRadius: '6px', padding: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ height: '24px', background: '#475569', borderRadius: '4px', width: '24px' }}></div>
            <div>
              <div style={{ height: '14px', background: '#475569', borderRadius: '4px', width: '60px', marginBottom: '4px' }}></div>
              <div style={{ height: '10px', background: '#475569', borderRadius: '4px', width: '80px' }}></div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Array.from({ length: 5 }).map((_, j) => (
              <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ height: '11px', background: '#475569', borderRadius: '4px', width: '48px' }}></div>
                <div style={{ height: '11px', background: '#475569', borderRadius: '4px', width: '36px' }}></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    
    <div style={{ borderTop: '1px solid #475569', paddingTop: '16px' }}>
      <div style={{ height: '11px', background: '#475569', borderRadius: '4px', width: '96px', marginBottom: '8px' }}></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ textAlign: 'center', background: '#334155', borderRadius: '6px', padding: '8px' }}>
            <div style={{ height: '16px', background: '#475569', borderRadius: '4px', marginBottom: '4px' }}></div>
            <div style={{ height: '9px', background: '#475569', borderRadius: '4px' }}></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Empty State
const EmptyFingerPerformanceBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖐️</div>
      <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
        No Finger Data
      </h3>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>
        No finger performance data available yet.
      </p>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        Practice more to see detailed finger analysis! ✋
      </div>
    </div>
  </div>
);

export default FingerPerformanceBlock;